import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import type { AppDispatch, RootState } from '/business/shared/state/store';
import formatTonnage from '/shared/functions/formatTonnage';

import type { QuestionConfigQuestionType } from '/index';
import type { ElectricityHeatSteamRenewableCertificate } from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';

import { urlApplicationRoot } from '/business/shared/util/Statics';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import useUserLanguage from '/business/shared/hooks/useUserLanguage';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectCertificatesSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectCertificatesSelector';
import getElectNumCertsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumCertsSelector';
import getElectNumLocationsForCertSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsForCertSelector';
import getElectCertsCalculationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectCertsCalculationsSelector';
import saveCertificate from '/business/shared/state/actions/basket/package2/saveCertificate';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';

import LinkButton from '/shared/components/widgets/button/LinkButton';
import Button from '/shared/components/widgets/button/Button';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';

import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledElecBreakout from './components/__styled__/StyledElecBreakout';
import StyledInfoList, { StyledInfoListItem } from './components/__styled__/StyledInfoList';
import StyledElecEmptyState from './components/__styled__/StyledElecEmptyState';
import StyledBasketTable from '/business/shared/components/widgets/basket-table/__styled__/StyledBasketTable';
import BasketClearItems from '/business/shared/components/widgets/basket-table/BasketClearItems';

const addPageLink = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/energy-attribute-certificates/energy-attribute-certificates-add`;

function CertificateRow({ certificate }: { certificate: ElectricityHeatSteamRenewableCertificate }) {
  const { t } = useTranslation();
  const lang = useUserLanguage();
  const dispatch = useDispatch<AppDispatch>();
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const { getUrlWithAppId } = useUrlBuilder();

  const appId = useSelector(getApplicationIdSelector);
  const numLocations = useSelector((state: RootState) => getElectNumLocationsForCertSelector(state, certificate.id));
  const dateStart = new Date(certificate.date_range_start_year, certificate.date_range_start_month).toLocaleString(
    lang,
    { month: 'short', year: 'numeric' },
  );

  const dateEnd = new Date(certificate.date_range_end_year, certificate.date_range_end_month).toLocaleString(lang, {
    month: 'short',
    year: 'numeric',
  });

  const handleRemove = useCallback(
    (cert: ElectricityHeatSteamRenewableCertificate) => {
      // @todo: Typescript for redux
      dispatch(
        saveCertificate({
          certificate: cert,
          applicationId: appId,
          isDelete: true,
        }),
      );
    },
    [dispatch, appId],
  );

  return (
    <tr key={`certificate-${certificate.id}`}>
      <td>{certificate.certificate_name}</td>
      <td>{certificate.certificate_id}</td>
      <td>
        {formatTonnage(certificate.energy_allocation_for_this_carbon_review_kwh) || 0}{' '}
        {t('business:emissionFactors.electricity.kwh')}
      </td>
      <td>
        <span>
          {dateStart} - {dateEnd}
        </span>
      </td>
      <td>{numLocations}</td>
      <td>
        <LinkButton
          disabled={!isEditable}
          $secondary
          to={getUrlWithAppId(`${addPageLink}?edit=${certificate.id}`, appId)}
        >
          {t('questions:electricityHeatSteam.shared.edit')}
        </LinkButton>
      </td>
      <td>
        <Button disabled={numLocations > 0 || !isEditable} $secondary onClick={() => handleRemove(certificate)}>
          {t('questions:electricityHeatSteam.shared.remove')}
        </Button>
      </td>
    </tr>
  );
}

function ElectricityHeatSteamCertsTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isEditable = useSelector(isApplicationAnswersEditableSelector);

  const appId = useSelector(getApplicationIdSelector);
  const certificates = useSelector(getElectCertificatesSelector);
  const handleRemoveBulk = useCallback(() => {
    certificates.map((cert: ElectricityHeatSteamRenewableCertificate) => {
      if ((cert.total_kwh_used_by_locations ?? 0) > 0) {
        return false;
      }
      dispatch(
        saveCertificate({
          certificate: cert,
          applicationId: appId,
          isDelete: true,
        }),
      );
    });
  }, [dispatch, appId, certificates]);
  return (
    <div style={{ marginTop: '3rem' }}>
      {isEditable && <BasketClearItems deleteCallbackButton={handleRemoveBulk} />}
      <StyledBasketTable>
        <thead>
          <tr>
            <th>{t('questions:electricityHeatSteam.certificates.table.certificateName')}</th>
            <th>{t('questions:electricityHeatSteam.certificates.table.certificateId')}</th>
            <th>{t('questions:electricityHeatSteam.certificates.table.kwhAllocation')}</th>
            <th>{t('questions:electricityHeatSteam.certificates.table.datesValid')}</th>
            <th>{t('questions:electricityHeatSteam.certificates.table.locationsApplied')}</th>
            <th>&nbsp;</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {certificates.map((certificate) => (
            <CertificateRow key={`certificate-${certificate.id}`} certificate={certificate} />
          ))}
        </tbody>
      </StyledBasketTable>
    </div>
  );
}

export default function ElectricityHeatSteamCerts({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const numCerts = useSelector(getElectNumCertsSelector);
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const { getUrlWithAppId } = useUrlBuilder();

  // @todo: These values are calculated with a front-end sum, but probably should come from backend maths.
  const { energyAssigned, energyAllocated, energyRemaining } = useSelector(getElectCertsCalculationsSelector);

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {/* @i18n: Electricity, heat and steam - Energy attribute certificates */}
          <Trans i18nKey="questions:electricityHeatSteam.certificates.title" />
        </StyledHeading>
        <StyledParagraph $largeText>
          {/* @i18n: If your business has valid energy attribute certificates for the reporting period[...] */}
          <Trans
            i18nKey="questions:electricityHeatSteam.certificates.intro"
            components={{ 1: <ReportingPeriod $green /> }}
          />
        </StyledParagraph>
        <StyledParagraph $smallText $hasExtraMarginBottom>
          {/* @i18n: e.g. Power Purchase Agreements (PPA), Renewable Energy Guarantee of Origin (REGO), or[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.certificates.example" />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.helpText" />
          </div>
        </StyledInfoFlash>
        {numCerts > 0 && (
          <LinkButton disabled={!isEditable} $primary to={getUrlWithAppId(addPageLink, appId)}>
            {/* @i18n: Add new energy attribute certificates */}
            <Trans i18nKey="questions:electricityHeatSteam.certificates.addNewCertificates" />
          </LinkButton>
        )}
      </div>

      {numCerts > 0 ? (
        <StyledElecBreakout>
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Energy attribute certificates summary at a glance */}
            <Trans i18nKey="questions:electricityHeatSteam.certificates.summaryTitle" />
          </StyledHeading>
          <StyledInfoList>
            <StyledInfoListItem>
              <div>{t('questions:electricityHeatSteam.shared.totalCertificatesAdded')}</div>
              <div>
                <div>{numCerts}</div>
                <div>&nbsp;</div>
              </div>
            </StyledInfoListItem>
            <StyledInfoListItem>
              <div>{t('questions:electricityHeatSteam.certificates.renewableEnergyAvailable')}</div>
              <div>
                <div>{formatTonnage(energyAllocated)}</div>
                <div>{t('business:emissionFactors.electricity.kwh')}</div>
              </div>
            </StyledInfoListItem>
            <StyledInfoListItem>
              <div>{t('questions:electricityHeatSteam.certificates.renewableEnergyAssigned')}</div>
              <div>
                <div>{formatTonnage(energyAssigned)}</div>
                <div>{t('business:emissionFactors.electricity.kwh')}</div>
              </div>
            </StyledInfoListItem>
            <StyledInfoListItem $isHighlighted>
              <div>{t('questions:electricityHeatSteam.certificates.renewableEnergyUnassigned')}</div>
              <div>
                <div>{formatTonnage(energyRemaining)}</div>
                <div>{t('business:emissionFactors.electricity.kwh')}</div>
              </div>
            </StyledInfoListItem>
          </StyledInfoList>

          <ElectricityHeatSteamCertsTable />
        </StyledElecBreakout>
      ) : (
        <StyledElecEmptyState>
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Add energy attribute certificate details */}
            <Trans i18nKey="questions:electricityHeatSteam.certificates.emptyTitle" />
          </StyledHeading>
          <StyledParagraph $smallText $hasExtraMarginBottom>
            {/* @i18n: Enter the certificate's name and how much renewable energy is available during the[...] */}
            <Trans i18nKey="questions:electricityHeatSteam.certificates.emptyBody" />
          </StyledParagraph>
          <LinkButton disabled={!isEditable} $primary to={getUrlWithAppId(addPageLink, appId)}>
            {/* @i18n: Add new energy attribute certificates */}
            <Trans i18nKey="questions:electricityHeatSteam.certificates.addNewCertificates" />
          </LinkButton>
        </StyledElecEmptyState>
      )}
    </>
  );
}
